import React from 'react';
import { Badge } from 'react-bootstrap';

/**
 * StatusBadge component provides consistent status badge styling across the app
 * Centralizes variant mapping for all status types
 *
 * @param {string} status - Status value (e.g., 'active', 'completed', 'rented', etc.)
 * @param {string} type - Status type category ('vehicle', 'rto', 'transaction', 'payment', 'general')
 * @param {string} customLabel - Optional custom label to display instead of status
 * @param {string} className - Additional CSS classes
 *
 * @example
 * <StatusBadge status="active" type="rto" />
 * <StatusBadge status="rented" type="vehicle" />
 * <StatusBadge status="late" type="rto" customLabel="Past Due" />
 */
export const StatusBadge = ({ status, type = 'general', customLabel, className = '' }) => {
  const getVariantAndLabel = () => {
    const normalizedStatus = status?.toLowerCase();

    // Vehicle statuses
    if (type === 'vehicle') {
      switch (normalizedStatus) {
        case 'available':
          return { variant: 'success', label: 'Available' };
        case 'rented':
          return { variant: 'danger', label: 'Rented' };
        case 'rto':
          return { variant: 'warning', label: 'RTO' };
        case 'maintenance':
          return { variant: 'secondary', label: 'Maintenance' };
        case 'sold':
          return { variant: 'primary', label: 'Sold' };
        default:
          return { variant: 'secondary', label: status };
      }
    }

    // RTO Agreement statuses
    if (type === 'rto') {
      switch (normalizedStatus) {
        case 'active':
        case 'current':
          return { variant: 'success', label: 'Current' };
        case 'completed':
          return { variant: 'primary', label: 'Completed' };
        case 'cancelled':
          return { variant: 'secondary', label: 'Cancelled' };
        case 'late':
        case 'delinquent':
          return { variant: 'danger', label: 'Late' };
        case 'pending':
          return { variant: 'warning', label: 'Pending' };
        default:
          return { variant: 'secondary', label: status };
      }
    }

    // Transaction statuses
    if (type === 'transaction') {
      switch (normalizedStatus) {
        case 'active':
        case 'open':
          return { variant: 'success', label: 'Active' };
        case 'completed':
        case 'closed':
          return { variant: 'primary', label: 'Completed' };
        case 'cancelled':
          return { variant: 'secondary', label: 'Cancelled' };
        case 'overdue':
          return { variant: 'danger', label: 'Overdue' };
        case 'pending':
          return { variant: 'warning', label: 'Pending' };
        default:
          return { variant: 'secondary', label: status };
      }
    }

    // Payment statuses
    if (type === 'payment') {
      switch (normalizedStatus) {
        case 'completed':
        case 'success':
          return { variant: 'success', label: 'Completed' };
        case 'pending':
          return { variant: 'warning', label: 'Pending' };
        case 'failed':
        case 'error':
          return { variant: 'danger', label: 'Failed' };
        case 'refunded':
          return { variant: 'info', label: 'Refunded' };
        default:
          return { variant: 'secondary', label: status };
      }
    }

    // Task priority statuses
    if (type === 'task') {
      switch (normalizedStatus) {
        case 'urgent':
          return { variant: 'danger', label: 'Urgent' };
        case 'high':
          return { variant: 'warning', label: 'High' };
        case 'medium':
          return { variant: 'info', label: 'Medium' };
        case 'low':
          return { variant: 'secondary', label: 'Low' };
        default:
          return { variant: 'secondary', label: status };
      }
    }

    // General/default statuses
    switch (normalizedStatus) {
      case 'active':
      case 'success':
      case 'approved':
        return { variant: 'success', label: 'Active' };
      case 'inactive':
      case 'disabled':
        return { variant: 'secondary', label: 'Inactive' };
      case 'pending':
      case 'processing':
        return { variant: 'warning', label: 'Pending' };
      case 'error':
      case 'failed':
      case 'rejected':
        return { variant: 'danger', label: 'Error' };
      case 'completed':
      case 'done':
        return { variant: 'primary', label: 'Completed' };
      default:
        return { variant: 'secondary', label: status || 'Unknown' };
    }
  };

  const { variant, label } = getVariantAndLabel();
  const displayLabel = customLabel || label;

  return (
    <Badge bg={variant} className={className}>
      {displayLabel}
    </Badge>
  );
};

export default StatusBadge;
